import {
	Box,
	CssBaseline,
	GlobalStyles,
	PaletteColorOptions,
	StepConnector,
	StepConnectorProps,
	StepIconProps,
} from '@mui/material';
import {
	ThemeProvider as MuiThemeProvider,
	Shadows,
	createTheme,
	extendTheme,
} from '@mui/material/styles';
import * as React from 'react';
import { TbCircleCheckFilled } from 'react-icons/tb';

// -------------------------------
// TypeScript Module Augmentation
// -------------------------------
declare module '@mui/material/styles' {
	interface TypographyVariants {
		'2xs': number | string;
		xs: number | string;
		sm: number | string;
		md: number | string;
		lg: number | string;
		xl: number | string;
		'2xl': number | string;
		'3xl': number | string;
		'4xl': number | string;
		'5xl': number | string;
		'6xl': number | string;
		'7xl': number | string;
	}
	interface TypographyVariantsOptions {
		'2xs'?: number | string;
		xs?: number | string;
		sm?: number | string;
		md?: number | string;
		lg?: number | string;
		xl?: number | string;
		'2xl'?: number | string;
		'3xl'?: number | string;
		'4xl'?: number | string;
		'5xl'?: number | string;
		'6xl'?: number | string;
		'7xl'?: number | string;
	}
}
declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		// add simple numeric font sizes similar to how Styled System used them
		'2xs': true;
		xs: true;
		sm: true;
		md: true;
		lg: true;
		xl: true;
		'2xl': true;
		'3xl': true;
		'4xl': true;
		'5xl': true;
		'6xl': true;
		'7xl': true;
	}
}
declare module '@mui/material/styles' {
	interface ColorRange {
		50: string;
		100: string;
		200: string;
		300: string;
		400: string;
		500: string;
		600: string;
		700: string;
		800: string;
		900: string;
	}

	interface Palette {
		gray: PaletteColor;
	}
	interface PaletteColor extends ColorRange {
		// add an additional less opaque color for various cases like hovers
		subtle?: string;
	}
	interface PaletteOptions {
		gray: PaletteColorOptions;
	}
}

// -------------------------------
// Define some custom JSX elements
// -------------------------------
const radioCheckedIcon = (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
			fill="currentColor"
		/>
	</svg>
);
const radioUncheckedIcon = (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<rect
			x="2.5"
			y="2.5"
			width="15"
			height="15"
			rx="7.5"
			stroke="currentColor"
		/>
	</svg>
);

const checkboxIcon = (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<rect
			x="2.5"
			y="2.5"
			width="15"
			height="15"
			rx="2.5"
			stroke="currentColor"
			strokeWidth={1.25}
		/>
	</svg>
);
const checkboxIndeterminateIcon = (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M5 2C3.34315 2 2 3.34315 2 5V15C2 16.6569 3.34315 18 5 18H15C16.6569 18 18 16.6569 18 15V5C18 3.34315 16.6569 2 15 2H5ZM7 9C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H7Z"
			fill="currentColor"
		/>
	</svg>
);
const checkboxCheckedIcon = (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M5 2C3.34315 2 2 3.34315 2 5V15C2 16.6569 3.34315 18 5 18H15C16.6569 18 18 16.6569 18 15V5C18 3.34315 16.6569 2 15 2H5ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
			fill="currentColor"
		/>
	</svg>
);

function CustomStepIcon(props: StepIconProps) {
	const { active, completed, className } = props;

	return (
		<Box
			sx={{
				color: 'gray.700',
				display: 'flex',
				height: 22,
				alignItems: 'center',
				...(active && {
					color: 'primary.400',
				}),
				...(completed && {
					color: 'primary.600',
				}),
			}}
		>
			{completed ? (
				<TbCircleCheckFilled fill="currentColor" />
			) : (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						position: 'relative',
						width: 16,
						height: 16,
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							backgroundColor: active ? 'primary.400' : 'gray.300',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							'&::before': {
								content: '""',
								position: 'absolute',
								width: 14,
								height: 14,
								zIndex: 0,
								borderRadius: '50%',
								opacity: 0.2,
								backgroundColor: active ? 'primary.400' : 'gray.300',
								transition: 'opacity 0.2s',
							},
						}}
					/>
				</Box>
			)}
		</Box>
	);
}

function CustomStepConnector(props: StepConnectorProps) {
	return (
		<StepConnector
			sx={{
				'& .MuiStepConnector-line': {
					borderColor: 'gray.300',
					borderTopWidth: 3,
					borderRadius: 1,
				},
			}}
		/>
	);
}

// -------------------------------
// Define shared tokens/constants
// -------------------------------
const primaryColorScale = {
	50: 'hsl(126, 49%, 97%)',
	100: 'hsl(126, 49%, 93%)',
	200: 'hsl(126, 49%, 87%)',
	300: 'hsl(126, 49%, 78%)',
	400: 'hsl(126, 49%, 68%)',
	500: 'hsl(126, 49%, 60%)',
	600: 'hsl(126, 49%, 54%)',
	700: 'hsl(126, 49%, 48%)',
	800: 'hsl(126, 49%, 40%)',
	900: 'hsl(126, 49%, 34%)',
	main: 'hsl(126, 49%, 47%)',
	subtle: 'hsla(126, 49%, 70%, 0.1)',
};
const secondaryColorScale = {
	50: 'hsl(210, 20%, 98%)',
	100: 'hsl(218, 14%, 96%)',
	200: 'hsl(218, 13%, 90%)',
	300: 'hsl(217, 12%, 84%)',
	400: 'hsl(218, 11%, 65%)',
	500: 'hsl(220, 10%, 46%)',
	600: 'hsl(216, 14%, 34%)',
	700: 'hsl(217, 18%, 26%)',
	800: 'hsl(215, 27%, 17%)',
	900: 'hsl(214, 100%, 13%)',
	main: 'hsl(214, 100%, 13%)',
	subtle: 'hsla(220, 11%, 94%, 0.75)',
};
const grayColorScale = {
	50: 'hsl(210, 20%, 98%)',
	100: 'hsl(218, 14%, 96%)',
	200: 'hsl(218, 13%, 90%)',
	300: 'hsl(217, 12%, 84%)',
	400: 'hsl(218, 11%, 65%)',
	500: 'hsl(220, 10%, 46%)',
	600: 'hsl(216, 14%, 34%)',
	700: 'hsl(217, 18%, 26%)',
	800: 'hsl(215, 27%, 17%)',
	900: 'hsl(218, 39%, 10%)',
	main: 'hsl(216, 14%, 34%)',
	subtle: 'hsla(220, 11%, 94%, 0.75)',
};
const lightPaletteScales = {
	background: {
		default: 'hsla(210, 20%, 99%, 1)',
		paper: '#fff',
	},
	primary: primaryColorScale,
	secondary: secondaryColorScale,
	gray: grayColorScale,
} as const;

// create the typography scale in pixels based on a 16px base font size
const typographySizesPx = {
	'2xs': 10,
	xs: 12,
	sm: 14,
	md: 16,
	lg: 18,
	xl: 20,
	'2xl': 24,
	'3xl': 30,
	'4xl': 36,
	'5xl': 48,
	'6xl': 60,
	'7xl': 72,
} as const;
type TypographySizes = keyof typeof typographySizesPx;

// -------------------------------
// Step 1: Define theme tokens
// -------------------------------
const customTheme = createTheme({
	cssVariables: {
		colorSchemeSelector: 'class',
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 720,
			md: 960,
			lg: 1240,
			xl: 1920,
		},
	},
	// overwrite the shadows since 25 is way too many options, use softer shadows so they don't get overdone
	shadows: [
		'none',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)', // border-like
		'0 1px 3px rgb(44 42 38 / 7%), 0 4px 16px rgb(44 42 38 / 6%)', // xs
		'0 1px 4px rgb(44 42 38 / 7%), 0 4px 24px rgb(44 42 38 / 7%)', // sm
		'0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // md
		'0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // lg
		'0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // xl
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
		'0 1px 2px 0 rgb(0 0 0 / 0.05)',
	] as Shadows,
	transitions: {
		duration: {
			// for micro interactions
			short: 150,
			// basic (default) timing
			standard: 200,
			// for complex animations
			complex: 300,
		},
	},
	// allow styling individual fontSizes in a component with the fontSize prop: "xs", "sm", "md", etc.
	typography: {
		fontSize: 14,
		'2xs': '0.625rem',
		xs: '0.75rem',
		sm: '0.875rem',
		md: '1rem',
		lg: '1.125rem',
		xl: '1.25rem',
		'2xl': '1.5rem',
		'3xl': '1.875rem',
		'4xl': '2.25rem',
		'5xl': '3rem',
		'6xl': '3.75rem',
		'7xl': '4.5rem',
		// use Inter, and then the default MacOS font stack
		fontFamily: [
			'"Inter"',
			'-apple-system',
			'BlinkMacSystemFont',
			'"Segoe UI"',
			'"Helvetica Neue"',
			'Arial',
			'sans-serif',
			'"Apple Color Emoji"',
			'"Segoe UI Emoji"',
			'"Segoe UI Symbol"',
		].join(','),
		h1: {
			fontSize: '1.5rem',
			fontWeight: 600,
			lineHeight: 78 / 70,
		},
		h2: {
			fontSize: '1.25rem',
			fontWeight: 600,
			lineHeight: 44 / 36,
		},
		h3: {
			fontSize: '1rem',
			fontWeight: 600,
			lineHeight: 44 / 36,
			letterSpacing: 0,
		},
		h4: {
			fontSize: '0.75rem',
			lineHeight: 42 / 28,
			letterSpacing: 0,
		},
	},
});

export const allAvailColorScales = {
	primary: {
		value: 'primary',
		label: 'Primary',
		colorScale: {
			light: lightPaletteScales.primary,
		},
	},
	secondary: {
		value: 'secondary',
		label: 'Secondary',
		colorScale: {
			light: lightPaletteScales.secondary,
		},
	},
	gray: {
		value: 'gray',
		label: 'Gray',
		colorScale: {
			light: lightPaletteScales.gray,
		},
	},
} as const;
export const allRadiusScales = {
	none: {
		label: 'None',
		amount: 0,
		value: 'none',
		description: 'Developer focused',
	},
	sm: {
		label: 'Small',
		amount: 3,
		value: 'sm',
		description: 'Elegant and refined',
	},
	md: {
		label: 'Medium',
		amount: 6,
		value: 'md',
		description: 'Balanced and versatile',
	},
	lg: {
		label: 'Large',
		amount: 12,
		value: 'lg',
		description: 'Friendly and easy',
	},
} as const;

// -------------------------------
// Lastly, create a Theme Provider
// -------------------------------
export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const activeColorScale = 'primary';
	const activeRadiusScale = 'md';
	const activeFont = 'Inter';
	const activeRadius = allRadiusScales[activeRadiusScale];

	const themeWithNewPalette = {
		...customTheme,
		colorSchemes: {
			light: {
				palette: {
					...customTheme.palette,
					...lightPaletteScales,
					primary: allAvailColorScales[activeColorScale].colorScale.light,
					divider: lightPaletteScales.gray[200],
				},
			},
		},
	};
	delete themeWithNewPalette?.vars;
	const theme = extendTheme(themeWithNewPalette, {
		spacing: '8px',
		typography: {
			fontFamily: `"${activeFont}",${customTheme.typography.fontFamily}`,
			body1: {
				fontFamily: `"${activeFont}",${customTheme.typography.fontFamily}`,
			},
			body2: {
				fontFamily: `"${activeFont}",${customTheme.typography.fontFamily}`,
			},
			button: {
				fontFamily: `"${activeFont}",${customTheme.typography.fontFamily}`,
			},
			caption: {
				fontFamily: `"${activeFont}",${customTheme.typography.fontFamily}`,
			},
		},
		shape: {
			borderRadius: `${activeRadius.amount}px`,
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: lightPaletteScales.background.default,
						color: lightPaletteScales.secondary['900'],
						// prevent font size jumping when font is loading
						fontDisplay: 'swap',
					},
					a: {
						color: lightPaletteScales.primary['600'],
						textDecoration: 'none',
						fontWeight: 500,
						'&:hover': {
							color: lightPaletteScales.primary['700'],
						},
					},
					'h1, h2, h3, h4, h5, h6': {
						color: lightPaletteScales.secondary['800'],
					},
					' input:focus, textarea:focus,': {
						outline: 'none',
					},
				},
			},
			MuiAccordion: {
				defaultProps: {
					disableGutters: true,
				},
				styleOverrides: {
					root: {
						borderStyle: 'none',
						background: lightPaletteScales.background.default,
						'&:not(:last-of-type)': {
							borderBottom: 0,
						},
						'&:before': {
							opacity: '1 !important',
							background: lightPaletteScales.secondary['200'],
						},
						'&.Mui-expanded': {
							margin: 0,
						},
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						padding: 0,
						fontWeight: 500,
						'&.Mui-expanded': {
							color: lightPaletteScales.primary['700'],
						},
					},
				},
			},
			MuiAccordionDetails: {
				styleOverrides: {
					root: {
						paddingLeft: 0,
						paddingRight: 0,
						paddingTop: 0,
						color: lightPaletteScales.secondary['600'],
						'p:not(:last-of-type)': {
							marginBottom: customTheme.spacing(1),
						},
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					root: ({ ownerState }: { ownerState: any }) => ({
						...(ownerState.severity === 'info' && {
							'.MuiAlert-icon': {
								color: lightPaletteScales.blue['600'],
							},
							backgroundColor: lightPaletteScales.primary['50'],
							color: lightPaletteScales.secondary['600'],
							border: `1px solid ${lightPaletteScales.primary['200']}`,
						}),
					}),
				},
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						// flatten the app bar by default
						boxShadow: customTheme.shadows[0],
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					inputRoot: {
						// specifically adjusted to match other inputs
						paddingBottom: '6px !important ',
						paddingLeft: '12px !important ',
						paddingRight: '32px !important ',
						paddingTop: '6px !important ',
					},
					paper: {
						borderRadius: activeRadius.amount * 1.5,
						transform: 'translateY(4px) !important',
						boxShadow: customTheme.shadows[2],
					},
					listbox: {
						padding: customTheme.spacing(0.5),
						display: 'flex',
						flexDirection: 'column',
						gap: '2px',
					},
					option: {
						minHeight: 32,
						borderRadius: activeRadius.amount,
					},
				},
			},
			MuiButtonBase: {
				defaultProps: {
					// remove the infamous Material Design button ripple
					disableRipple: true,
				},
				styleOverrides: {
					root: {
						'&': {
							lineHeight: 1.6,
						},
						color: lightPaletteScales.secondary['700'],
					},
				},
			},
			MuiButton: {
				defaultProps: {
					disableElevation: true,
				},
				styleOverrides: {
					root: ({ ownerState }: { ownerState: any }) => ({
						...((ownerState.variant === 'contained' ||
							ownerState.variant === 'outlined') && {
							boxShadow: customTheme.shadows[1],
						}),
						...(ownerState.variant === 'contained' &&
							ownerState.color === 'primary' && {
								transition: customTheme.transitions.create('all'),
								fontWeight: 600,
								'&:hover': {
									filter: 'contrast(1.1)',
								},
								color: theme.palette.background.paper,
							}),
						...(ownerState.variant === 'outlined' &&
							(ownerState.color === 'gray' ||
								ownerState.color === 'secondary') && {
								background: lightPaletteScales.background.paper,
								borderColor: lightPaletteScales.secondary['300'],
								'&:hover': {
									background: lightPaletteScales.secondary['50'],
									borderColor: lightPaletteScales.secondary['300'],
									boxShadow: customTheme.shadows[1],
								},
							}),
						cursor: 'pointer',
						textTransform: 'inherit',
						letterSpacing: 0.2,
						borderRadius: activeRadius.amount * 1.25,
					}),
				},
			},
			MuiCardActions: {
				styleOverrides: {
					root: {
						background: lightPaletteScales.secondary['100'],
						color: lightPaletteScales.secondary['500'],
						justifyContent: 'flex-end',
						transition: customTheme.transitions.create('background'),
						fontWeight: '500',
						'&:hover': {
							background: lightPaletteScales.secondary['50'],
						},
					},
				},
			},
			MuiCardContent: {
				styleOverrides: {
					root: {
						padding: customTheme.spacing(2),
						'&:last-of-type': {
							paddingBottom: customTheme.spacing(2),
						},
					},
				},
			},
			MuiCardHeader: {
				styleOverrides: {
					title: {
						fontSize: customTheme.typography.xl,
						fontWeight: '500',
						color: lightPaletteScales.secondary['700'],
					},
					action: {
						alignSelf: 'center',
						marginRight: 0,
						fontSize: customTheme.typography.sm,
						color: lightPaletteScales.secondary['500'],
					},
				},
			},
			MuiChartsAxisHighlight: {
				styleOverrides: {
					root: {
						stroke: lightPaletteScales.secondary['300'],
					},
				},
			},
			MuiHighlightElement: {
				styleOverrides: {
					root: {
						stroke: lightPaletteScales.background.default,
						strokeWidth: 2,
					},
				},
			},
			MuiChartsTooltip: {
				styleOverrides: {
					row: {
						'.MuiChartsTooltip-cell': {
							borderBottomColor: lightPaletteScales.secondary['200'],
						},
					},
					table: {
						border: `1px solid ${lightPaletteScales.secondary['200']}`,
						borderRadius: activeRadius.amount,
						boxShadow: customTheme.shadows[4],
					},
					valueCell: {
						fontSize: customTheme.typography.xs,
						paddingLeft: customTheme.spacing(1),
					},
				},
			},
			MuiCheckbox: {
				defaultProps: {
					icon: checkboxIcon,
					indeterminateIcon: checkboxIndeterminateIcon,
					checkedIcon: checkboxCheckedIcon,
				},
				styleOverrides: {
					root: {
						borderRadius: activeRadius.amount,
					},
				},
			},
			MuiContainer: {
				defaultProps: {
					maxWidth: false,
				},
				styleOverrides: {
					root: {
						// Removed fixed maxWidth constraint to allow responsive sizing
						// Individual components can set their own maxWidth as needed
					},
				},
			},
			MuiDataGrid: {
				styleOverrides: {
					row: {
						borderTopColor: lightPaletteScales.secondary['200'],
						backgroundColor: lightPaletteScales.background.paper,
						'&:hover': {
							backgroundColor: lightPaletteScales.secondary['50'],
						},
						'&.Mui-selected': {
							backgroundColor: lightPaletteScales.secondary['50'],
							'&:hover': {
								backgroundColor: lightPaletteScales.primary['subtle'],
								filter: 'contrast(0.9)',
							},
						},
					},
					columnHeaders: {
						borderWidth: 0.5,
					},
					footerContainer: {
						borderWidth: 0.5,
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						borderRadius: activeRadius.amount * 2,
						padding: customTheme.spacing(3),
					},
				},
			},
			MuiDivider: {
				styleOverrides: {
					root: {
						borderBottomWidth: 0.5,
						borderColor: lightPaletteScales.secondary['200'],
					},
				},
			},
			MuiIconButton: {
				defaultProps: {
					size: 'small',
				},
				styleOverrides: {
					root: {
						borderRadius: activeRadius.amount * 1.5,
					},
				},
			},
			MuiInputBase: {
				defaultProps: {
					size: 'small',
				},
				styleOverrides: {
					root: {
						backgroundColor: lightPaletteScales.background.paper,
						// borderColor: lightPaletteScales.secondary['300'],
						boxShadow: `${customTheme.shadows[1]}`,
						// borderWidth: 1,
						borderStyle: 'solid',
						borderRadius: activeRadius.amount,
					},
				},
			},
			MuiFormControl: {
				defaultProps: {
					variant: 'filled',
				},
				styleOverrides: {
					root: {
						borderColor: lightPaletteScales.secondary['300'],
					},
				},
			},
			MuiFormLabel: {
				styleOverrides: {
					root: {
						fontSize: customTheme.typography.sm,
						fontWeight: '500',
						color: lightPaletteScales.secondary['800'],
						marginBottom: customTheme.spacing(1.5),
						'&.Mui-focused': {
							color: 'inherit !important',
						},
					},
				},
			},
			MuiFilledInput: {
				defaultProps: {
					hiddenLabel: true,
					disableUnderline: true,
				},
				styleOverrides: {
					root: {
						backgroundColor: lightPaletteScales.background.paper,
						borderRadius: activeRadius.amount,
						'&.Mui-focused': {
							backgroundColor: lightPaletteScales.background.paper,
							borderColor: lightPaletteScales.primary['400'],
						},
						'&:hover:not(.Mui-disabled)': {
							backgroundColor: lightPaletteScales.background.paper,
						},
						'&.Mui-disabled': {
							backgroundColor: lightPaletteScales.secondary['100'],
						},
					},
				},
			},
			MuiGridFilterForm: {
				styleOverrides: {
					closeIcon: {
						minWidth: 30,
					},
				},
			},
			MuiListItemText: {
				styleOverrides: {
					root: {
						'.MuiListItemText-primary': {
							fontWeight: '500',
							color: lightPaletteScales.secondary['700'],
						},
					},
				},
			},
			MuiMenu: {
				defaultProps: {
					elevation: 2,
				},
				styleOverrides: {
					list: {
						padding: customTheme.spacing(0.5),
						// mui applies a 0 margin to the right of all lists that can put things off balance
						paddingRight: `${customTheme.spacing(0.5)} !important`,
						display: 'flex',
						flexDirection: 'column',
						gap: '2px',
					},
					paper: {
						borderRadius: `${activeRadius.amount * 2}px !important`,
					},
				},
			},
			MuiMenuItem: {
				defaultProps: {
					dense: true,
				},
				styleOverrides: {
					root: {
						columnGap: customTheme.spacing(1),
						borderRadius: activeRadius.amount,
						paddingLeft: customTheme.spacing(1),
						paddingRight: customTheme.spacing(1),
						'&:focus': {
							backgroundColor: lightPaletteScales.secondary['subtle'],
							'&.Mui-selected': {
								// @ts-ignore
								backgroundColor: lightPaletteScales.primary['subtle'],
							},
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						borderColor: lightPaletteScales.secondary['300'],
					},
				},
			},
			MuiPaper: {
				defaultProps: {
					elevation: 0,
				},
				styleOverrides: {
					root: {
						borderColor: lightPaletteScales.secondary['200'],
						color: lightPaletteScales.secondary['800'],
						borderWidth: 0.5,
						borderStyle: 'solid',
					},
				},
			},
			MuiPopover: {
				defaultProps: {
					elevation: 1,
					transitionDuration: 225,
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'center',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'center',
					},
					PaperProps: {
						sx: {
							boxShadow: customTheme.shadows[2],
						},
					},
				},
				styleOverrides: {
					paper: {
						transform: 'translateY(4px) !important',
					},
				},
			},
			MuiRadio: {
				defaultProps: {
					icon: radioUncheckedIcon,
					checkedIcon: radioCheckedIcon,
				},
			},
			MuiNativeSelect: {
				defaultProps: {
					variant: 'filled',
				},
				styleOverrides: {
					root: {
						borderColor: lightPaletteScales.secondary['300'],
					},
					select: {
						minWidth: 100,
						'&:focus': {
							backgroundColor: lightPaletteScales.background.paper,
						},
					},
				},
			},
			MuiSelect: {
				defaultProps: {
					variant: 'filled',
				},
				styleOverrides: {
					root: {
						borderColor: lightPaletteScales.secondary['300'],
					},
					select: {
						minWidth: 100,
						borderRadius: activeRadius.amount,
						'&:focus': {
							backgroundColor: lightPaletteScales.background.paper,
							borderRadius: activeRadius.amount,
						},
					},
				},
			},
			MuiStepper: {
				defaultProps: {
					connector: <CustomStepConnector />,
				},
				styleOverrides: {
					root: {
						'& .Mui-active': {
							'& .MuiStepConnector-line': {
								borderColor: lightPaletteScales.primary['200'],
							},
						},
						'& .Mui-completed': {
							'& .MuiStepConnector-line': {
								borderColor: lightPaletteScales.primary['400'],
							},
						},
					},
				},
			},
			MuiStepLabel: {
				defaultProps: {
					StepIconComponent: CustomStepIcon,
				},
			},
			MuiSwitch: {
				styleOverrides: {
					root: {
						width: 42,
						height: 26,
						padding: 0,
						'& .MuiSwitch-switchBase': {
							padding: 0,
							margin: 2,
							transitionDuration: '200ms',
							'&.Mui-checked': {
								transform: 'translateX(16px)',
								color: lightPaletteScales.background.paper,
								'& + .MuiSwitch-track': {
									backgroundColor: lightPaletteScales.secondary['600'],
									opacity: 1,
									border: 0,
								},
								'&.Mui-disabled + .MuiSwitch-track': {
									opacity: 0.5,
								},
							},
							'&.Mui-focusVisible .MuiSwitch-thumb': {
								color: lightPaletteScales.secondary['500'],
								border: `6px solid ${lightPaletteScales.background.paper}`,
							},
							'&.Mui-disabled .MuiSwitch-thumb': {
								color: lightPaletteScales.secondary[100],
							},
							'&.Mui-disabled + .MuiSwitch-track': {
								opacity: 0.7,
							},
						},
						'& .MuiSwitch-thumb': {
							boxSizing: 'border-box',
							width: 22,
							height: 22,
						},
						'& .MuiSwitch-track': {
							borderRadius: 26 / 2,
							backgroundColor: lightPaletteScales.background.default,
							opacity: 1,
						},
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					root: {
						borderColor: lightPaletteScales.secondary['200'],
						borderWidth: 0.5,
					},
				},
			},
			MuiTablePagination: {
				styleOverrides: {
					select: {
						paddingTop: '0 !important',
						paddingBottom: 0,
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						textTransform: 'none',
						paddingLeft: 0,
						paddingRight: 0,
						minWidth: 60,
					},
				},
			},
			MuiTabs: {
				styleOverrides: {
					flexContainer: {
						gap: customTheme.spacing(4),
					},
				},
			},
			MuiTooltip: {
				defaultProps: {
					arrow: true,
				},
				styleOverrides: {
					tooltip: {
						color: lightPaletteScales.secondary['50'],
						background: lightPaletteScales.secondary['900'],
					},
					arrow: {
						color: lightPaletteScales.secondary['900'],
					},
				},
			},
			MuiToggleButtonGroup: {
				styleOverrides: {
					root: {
						background: lightPaletteScales.background.paper,
					},
				},
			},
			MuiToggleButton: {
				styleOverrides: {
					root: {
						gap: 6,
						textTransform: 'inherit',
						'&.Mui-selected': {
							color: lightPaletteScales.primary['700'],
							fontWeight: 700,
						},
						'&.Mui-disabled': {
							color: lightPaletteScales.secondary['300'],
						},
					},
					sizeSmall: {
						paddingTop: customTheme.spacing(0.25),
						paddingBottom: customTheme.spacing(0.25),
						paddingLeft: customTheme.spacing(1),
						paddingRight: customTheme.spacing(1),
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: ({
						ownerState,
					}: {
						ownerState: {
							sx: { fontSize: TypographySizes };
						};
					}) => {
						let tracking: number | string = 'inherit';
						const sizeToken: TypographySizes | undefined =
							ownerState?.sx?.fontSize;
						if (sizeToken in typographySizesPx) {
							const pixelSize = typographySizesPx[ownerState.sx.fontSize];
							// automatically apply suggest dynamic tracking based on recommendations for Inter:
							const a = -0.0223;
							const b = 0.185;
							const c = -0.1745;
							let trackingAmount = a + b * Math.E ** (c * pixelSize);
							if (trackingAmount < 1.25) {
								trackingAmount = 1.25;
							}
							tracking = `${trackingAmount}em`;
						}
						return {
							color: lightPaletteScales.secondary['800'],
							lineHeight: tracking,
						};
					},
				},
			},
		},
	});

	return (
		<MuiThemeProvider theme={theme}>
			{/* Set global MUI styles */}
			<GlobalStyles
				styles={{
					'*, *::before, *::after': {
						boxSizing: 'border-box',
					},
					html: {
						minHeight: '100vh',
					},
					body: {
						minHeight: '100vh',
					},
					'.MuiTablePagination-select': {
						paddingBottom: '1px !important',
					},
					'.MuiDataGrid-menuList': {
						'&&&': {
							padding: 4,
						},
					},
				}}
			/>
			<CssBaseline />
			{children}
		</MuiThemeProvider>
	);
}
